import { useEffect, useRef } from 'react';
import type { ClusterDetail } from '@ghostwright/api-client';

interface BriefProgressDrawerProps {
  clusters: ClusterDetail[];
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_ICONS: Record<ClusterDetail['status'], string> = {
  done: '●',
  active: '◐',
  parked: '○',
  partial: '◔',
  pending: '○',
};

const STATUS_COLORS: Record<ClusterDetail['status'], string> = {
  done: 'text-green-700',
  active: 'text-primary',
  parked: 'text-muted-foreground',
  partial: 'text-amber-600',
  pending: 'text-border',
};

export default function BriefProgressDrawer({ clusters, isOpen, onClose }: BriefProgressDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const done = clusters.filter((c) => c.status === 'done').length;
  const total = clusters.length;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40 sm:hidden" onClick={onClose} />

      {/* Mobile: bottom sheet */}
      <div
        ref={drawerRef}
        className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border rounded-t-2xl p-5 pb-8 shadow-lg sm:hidden animate-in slide-in-from-bottom duration-200"
      >
        <div className="w-10 h-1 bg-border rounded-full mx-auto mb-4" />
        <DrawerContent clusters={clusters} done={done} total={total} />
      </div>

      {/* Desktop: right panel */}
      <div
        ref={drawerRef}
        className="hidden sm:block fixed top-0 right-0 bottom-0 z-50 w-[300px] bg-background border-l border-border shadow-lg p-6 overflow-y-auto animate-in slide-in-from-right duration-200"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-foreground">Brief Progress</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg leading-none"
          >
            &times;
          </button>
        </div>
        <DrawerContent clusters={clusters} done={done} total={total} />
      </div>
    </>
  );
}

function DrawerContent({ clusters, done, total }: { clusters: ClusterDetail[]; done: number; total: number }) {
  return (
    <>
      <div className="text-xs text-muted-foreground mb-4">
        {done}/{total} topics covered
      </div>
      <ul className="space-y-2">
        {clusters.map((cluster) => (
          <li
            key={cluster.id}
            className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
              cluster.status === 'active' ? 'bg-primary/5 border border-primary/20' : ''
            }`}
          >
            <span className={`text-sm ${STATUS_COLORS[cluster.status]}`}>
              {STATUS_ICONS[cluster.status]}
            </span>
            <span className={`text-sm ${cluster.status === 'active' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
              {cluster.name}
            </span>
            {cluster.status === 'parked' && (
              <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">parked</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
